import React from "react";
import {
	Loader2,
	Heart,
	Calendar,
	MessageSquare,
	DollarSign,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Generic loading spinner
export const LoadingSpinner: React.FC<{ size?: "sm" | "md" | "lg" }> = ({
	size = "md",
}) => {
	const sizeClasses = {
		sm: "w-4 h-4",
		md: "w-6 h-6",
		lg: "w-8 h-8",
	};

	return (
		<div className="flex items-center justify-center">
			<Loader2
				className={`${sizeClasses[size]} animate-spin text-primary-600`}
			/>
		</div>
	);
};

// Page loading with brand elements
export const PageLoading: React.FC = () => (
	<div className="min-h-screen flex items-center justify-center bg-gray-50">
		<div className="text-center">
			<div className="mb-4">
				<img
					src="https://d64gsuwffb70l.cloudfront.net/686337322e4231e379c159ce_1754533566867_50f4f8d6.png"
					alt="GaiaBirth"
					className="w-12 h-12 mx-auto animate-pulse object-contain"
				/>
			</div>
			<h2 className="text-xl font-semibold text-gray-900 mb-2">
				GaiaBirth.com
			</h2>
			<LoadingSpinner size="lg" />
			<p className="text-gray-600 mt-2">Loading your experience...</p>
		</div>
	</div>
);

// Doula card skeleton
export const DoulaCardSkeleton: React.FC = () => (
	<Card className="p-6">
		<div className="flex items-start space-x-4">
			<Skeleton className="w-16 h-16 rounded-full" />
			<div className="flex-1 space-y-2">
				<Skeleton className="h-5 w-3/4" />
				<Skeleton className="h-4 w-1/2" />
				<div className="flex space-x-2">
					<Skeleton className="h-3 w-16" />
					<Skeleton className="h-3 w-16" />
				</div>
			</div>
		</div>
		<div className="mt-4 space-y-2">
			<Skeleton className="h-4 w-full" />
			<Skeleton className="h-4 w-2/3" />
		</div>
		<div className="mt-4 flex justify-between items-center">
			<Skeleton className="h-6 w-20" />
			<Skeleton className="h-8 w-24" />
		</div>
	</Card>
);

// Dashboard stats skeleton
export const DashboardStatsSkeleton: React.FC = () => (
	<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
		{[Calendar, MessageSquare, DollarSign, Heart].map((Icon, index) => (
			<Card key={index} className="p-6">
				<div className="flex items-center justify-between">
					<div>
						<Skeleton className="h-4 w-20 mb-2" />
						<Skeleton className="h-8 w-16" />
					</div>
					<Icon className="w-8 h-8 text-gray-300" />
				</div>
			</Card>
		))}
	</div>
);

// Message thread skeleton
export const MessageSkeleton: React.FC = () => (
	<div className="space-y-4">
		{[1, 2, 3].map((i) => (
			<div
				key={i}
				className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}
			>
				<div
					className={`max-w-xs p-3 rounded-lg ${i % 2 === 0 ? "bg-primary-100" : "bg-gray-100"}`}
				>
					<Skeleton className="h-4 w-32 mb-1" />
					<Skeleton className="h-3 w-16" />
				</div>
			</div>
		))}
	</div>
);

// Table loading skeleton
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({
	rows = 5,
	cols = 4,
}) => (
	<div className="space-y-4">
		<div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-t-lg">
			{Array.from({ length: cols }).map((_, i) => (
				<Skeleton key={i} className="h-4 w-20" />
			))}
		</div>
		{Array.from({ length: rows }).map((_, rowIndex) => (
			<div key={rowIndex} className="grid grid-cols-4 gap-4 p-4 border-b">
				{Array.from({ length: cols }).map((_, colIndex) => (
					<Skeleton key={colIndex} className="h-4 w-24" />
				))}
			</div>
		))}
	</div>
);

// Form loading state
export const FormLoading: React.FC = () => (
	<div className="space-y-4">
		<Skeleton className="h-10 w-full" />
		<Skeleton className="h-10 w-full" />
		<Skeleton className="h-24 w-full" />
		<div className="flex justify-end space-x-2">
			<Skeleton className="h-10 w-20" />
			<Skeleton className="h-10 w-20" />
		</div>
	</div>
);

export default {
	LoadingSpinner,
	PageLoading,
	DoulaCardSkeleton,
	DashboardStatsSkeleton,
	MessageSkeleton,
	TableSkeleton,
	FormLoading,
};
